import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  TreeDeciduous, 
  Map, 
  Award, 
  Eye,
  ArrowRight,
  Leaf,
  Users,
  Shield,
  Sprout,
  TreePine,
  Flame,
  Heart
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { t } = useLanguage();
  const [stats, setStats] = React.useState({ 
    treesPlanted: 0, 
    treesAdopted: 0, 
    donors: 0, 
    co2Offset: 0 
  });
  const [categories, setCategories] = React.useState([]);

  const seedData = async () => {
    try {
      await fetch(`${API}/seed-all`, { method: 'POST' });
    } catch (e) {
      console.log('Seed skipped');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats({
          treesPlanted: data.trees?.planted || 0,
          treesAdopted: data.trees?.adopted || 0,
          donors: data.donations?.donor_count || 0,
          co2Offset: data.impact?.co2_offset_tons || 0
        });
      }
    } catch (e) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (e) {
      console.error('Failed to fetch categories');
    }
  };

  React.useEffect(() => {
    fetchStats();
    fetchCategories();
    seedData();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: { transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1448375240586-882707db888b?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Forest"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 text-6xl opacity-20 animate-leaf-sway">🌿</div>
        <div className="absolute bottom-40 right-20 text-5xl opacity-15 animate-leaf-sway" style={{ animationDelay: '1s' }}>🍃</div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="eco-badge mb-6 mx-auto w-fit">
            <Leaf className="w-3.5 h-3.5" />
            Patagonia, Argentina
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6" data-testid="hero-title">
            {t('landing.heroTitle')}
          </h1>
          <p className="text-xl sm:text-2xl text-white/80 mb-10 font-light max-w-2xl mx-auto" data-testid="hero-subtitle">
            {t('landing.heroSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-white text-lg px-8 py-6 rounded-md shadow-xl"
              data-testid="hero-cta-adopt"
            >
              <Link to="/adopt">
                <Sprout className="w-5 h-5 mr-2" />
                {t('landing.heroCta')}
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg" 
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-lg px-8 py-6 rounded-md backdrop-blur-sm"
              data-testid="hero-cta-explore"
            >
              <Link to="/map">
                <Map className="w-5 h-5 mr-2" />
                {t('landing.heroExplore')}
              </Link>
            </Button>
          </div>
          
          {/* Scroll to roadmap hint */}
          <div className="mt-8">
            <a href="#what-happened" className="text-white/60 hover:text-white/80 text-sm flex items-center gap-2 justify-center transition-colors">
              <span>{t('landing.viewRoadmap')}</span>
              <ArrowRight className="w-4 h-4 rotate-90" />
            </a>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* What Happened to Epuyén Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-stone-900 to-stone-800" id="what-happened" data-testid="what-happened-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8 text-center" data-testid="what-happened-title">
              {t('landing.whatHappened.title')}
            </h2>
            
            <div className="space-y-6 text-lg text-stone-300 leading-relaxed">
              <p className="text-xl text-orange-400 font-semibold">
                {t('landing.whatHappened.year')}
              </p>
              
              <p className="text-2xl text-white font-bold">
                {t('landing.whatHappened.hectares')}
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>{t('landing.whatHappened.ecosystems')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>{t('landing.whatHappened.homes')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>{t('landing.whatHappened.wildlife')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>{t('landing.whatHappened.landExposed')}</span>
                </div>
              </div>
              
              <p>{t('landing.whatHappened.balanceBroken')}</p>
              
              <p className="text-stone-400 italic">
                {t('landing.whatHappened.minimalEfforts')}
              </p>
              
              <p className="text-white font-semibold text-xl py-4">
                {t('landing.whatHappened.peopleStayed')}
              </p>
              
              <p>{t('landing.whatHappened.todayScars')}</p>
              
              <div className="bg-primary/10 border-l-4 border-primary p-6 rounded-r-lg mt-8">
                <p className="text-white">
                  {t('landing.whatHappened.epuyenCreated')}
                </p>
              </div>
              
              <p className="text-center text-xl text-white font-light pt-6">
                {t('landing.whatHappened.fromAshes')}
              </p>
            </div>
            
            <div className="flex justify-center mt-10">
              <a href="#roadmap" className="text-primary hover:text-primary/80 flex items-center gap-2 transition-colors">
                <span>{t('landing.viewRoadmap')}</span>
                <ArrowRight className="w-4 h-4 rotate-90" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Challenges Section */}
      <section className="py-24 px-4 bg-card/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-12">
              {t('landing.coreChallenges.title')}
            </h2>
            
            <div className="space-y-8 text-muted-foreground">
              {/* Main Challenge - De-pining */}
              <div className="bg-background/80 rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <TreePine className="w-5 h-5 text-amber-500" />
                  </span>
                  De-Pining
                </h3>
                <p className="mb-4">{t('landing.coreChallenges.mainChallenge')}</p>
                <p className="mb-4">{t('landing.coreChallenges.effectiveMethod')}</p>
                <p className="mb-4">{t('landing.coreChallenges.processRestores')}</p>
                <p className="text-foreground font-medium">{t('landing.coreChallenges.parallelReplanting')}</p>
              </div>

              {/* Second Mission - Fire Prevention */}
              <div className="bg-background/80 rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-red-500" />
                  </span>
                  {t('landing.coreChallenges.secondMission')}
                </h3>
                <p className="mb-4">{t('landing.coreChallenges.moreThanPlanting')}</p>
                
                <p className="font-medium text-foreground mb-3">{t('landing.coreChallenges.foundationInvest')}</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    {t('landing.coreChallenges.investList.machinery')}
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    {t('landing.coreChallenges.investList.equipment')}
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    {t('landing.coreChallenges.investList.tools')}
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    {t('landing.coreChallenges.investList.infrastructure')}
                  </li>
                </ul>
                
                <p className="text-amber-600 dark:text-amber-400 font-medium">{t('landing.coreChallenges.withoutEquipment')}</p>
              </div>

              {/* Breaking the Cycle */}
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-8 text-center">
                <p className="text-lg text-foreground font-medium">
                  {t('landing.coreChallenges.breakCycle')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Donation Categories Section */}
      <section className="py-24 px-4 bg-background" id="donate">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Heart className="w-3.5 h-3.5" />
              {t('landing.donationCategories.title')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t('landing.donationCategories.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.donationCategories.subtitle')}
            </p>
          </motion.div>

          {/* Five Category Cards with Progress Bars - Dynamic from Backend */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {(categories.length > 0 ? categories : [
              { slug: 'depining', goal: 19600000, raised: 0, icon: '🟫', name_en: 'Machinery for De-Pining', color: 'amber' },
              { slug: 'firefighting', goal: 1450000, raised: 0, icon: '🚒', name_en: 'Firefighting Boat', color: 'red' },
              { slug: 'reforestation', goal: 250000, raised: 0, icon: '🌱', name_en: 'Reforestation', color: 'green' },
              { slug: 'research', goal: 50000, raised: 0, icon: '🔬', name_en: 'Research Phase', color: 'blue' },
              { slug: 'biosphere', goal: 1150000, raised: 0, icon: '🏛️', name_en: 'Biosphere Center', color: 'purple' }
            ]).map((cat, index) => {
              const categoryConfig = {
                depining: {
                  translationKey: 'category1',
                  route: '/donate/depining',
                  colorClasses: {
                    border: 'hover:border-amber-500/50',
                    text: 'text-amber-600 dark:text-amber-400',
                    groupHover: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
                    arrowHover: 'group-hover:text-amber-500',
                    bg: 'bg-amber-500'
                  }
                },
                firefighting: {
                  translationKey: 'category2',
                  route: '/donate/firefighting',
                  colorClasses: {
                    border: 'hover:border-red-500/50',
                    text: 'text-red-600 dark:text-red-400',
                    groupHover: 'group-hover:text-red-600 dark:group-hover:text-red-400',
                    arrowHover: 'group-hover:text-red-500',
                    bg: 'bg-red-500'
                  }
                },
                reforestation: {
                  translationKey: 'category3',
                  route: '/donate/reforestation',
                  colorClasses: {
                    border: 'hover:border-primary/50',
                    text: 'text-primary',
                    groupHover: 'group-hover:text-primary',
                    arrowHover: 'group-hover:text-primary',
                    bg: 'bg-primary'
                  }
                },
                research: {
                  translationKey: 'category4',
                  route: '/donate/contamination',
                  colorClasses: {
                    border: 'hover:border-cyan-500/50',
                    text: 'text-cyan-600 dark:text-cyan-400',
                    groupHover: 'group-hover:text-cyan-600 dark:group-hover:text-cyan-400',
                    arrowHover: 'group-hover:text-cyan-500',
                    bg: 'bg-cyan-500'
                  },
                  isResearch: true
                },
                biosphere: {
                  translationKey: 'category5',
                  route: '/biosphere',
                  colorClasses: {
                    border: 'hover:border-purple-500/50',
                    text: 'text-purple-600 dark:text-purple-400',
                    groupHover: 'group-hover:text-purple-600 dark:group-hover:text-purple-400',
                    arrowHover: 'group-hover:text-purple-500',
                    bg: 'bg-purple-500'
                  }
                }
              };

              const config = categoryConfig[cat.slug] || categoryConfig.reforestation;
              const raised = cat.raised || 0;
              const goal = cat.goal || 1;
              const percentage = Math.round((raised / goal) * 1000) / 10;
              const goalDisplay = goal >= 1000000 
                ? `$${(goal / 1000000).toFixed(1)}M` 
                : `$${goal.toLocaleString()}`;

              return (
                <Link key={cat.slug} to={config.route}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * (index + 1) }}
                    className={`bg-card rounded-2xl p-6 border border-border ${config.colorClasses.border} hover:shadow-lg transition-all cursor-pointer group h-full`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-4xl">{t(`landing.donationCategories.${config.translationKey}.icon`)}</div>
                      <ArrowRight className={`w-5 h-5 text-muted-foreground ${config.colorClasses.arrowHover} transition-colors`} />
                    </div>
                    <h3 className={`text-lg font-bold text-foreground mb-2 ${config.colorClasses.groupHover} transition-colors`}>
                      {t(`landing.donationCategories.${config.translationKey}.title`)}
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm line-clamp-2">
                      {t(`landing.donationCategories.${config.translationKey}.description`)}
                    </p>
                    
                    {/* Progress Bar or Research Badge */}
                    <div className="mt-auto">
                      {config.isResearch ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 rounded-full">
                          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
                          <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">
                            {t(`landing.donationCategories.${config.translationKey}.researchPhase.currentPhase`) || 'Research & Diagnostic Phase'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">${raised.toLocaleString()}</span>
                            <span className={`${config.colorClasses.text} font-medium`}>{percentage}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full ${config.colorClasses.bg} rounded-full`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{t('roadmap.goal')}: {goalDisplay}</p>
                        </>
                      )}
                    </div>
                    
                    <p className={`text-sm font-medium ${config.colorClasses.text} mt-4`}>
                      {t(`landing.donationCategories.${config.translationKey}.impact`)}
                    </p>
                  </motion.div>
                </Link>
              );
            })}
          </div>

          {/* Transparency Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-card/50 rounded-2xl p-8 border border-border mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold text-foreground">
                {t('landing.donationCategories.transparency.title')}
              </h3>
            </div>
            <p className="text-muted-foreground mb-4">
              {t('landing.donationCategories.transparency.description')}
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {t('landing.donationCategories.transparency.items').map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Eye className="w-4 h-4 text-primary flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <p className="text-foreground font-medium">
              {t('landing.donationCategories.transparency.verify')}
            </p>
          </motion.div>

          {/* One System, Five Missions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-amber-500/10 via-red-500/10 via-green-500/10 via-cyan-500/10 to-purple-500/10 rounded-2xl p-8 text-center border border-border"
          >
            <h3 className="text-2xl font-bold text-foreground mb-6">
              {t('landing.donationCategories.oneSystem.title')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="text-center">
                <span className="text-3xl mb-2 block">🟫</span>
                <p className="text-foreground font-medium text-sm">{t('landing.donationCategories.oneSystem.depining')}</p>
              </div>
              <div className="text-center">
                <span className="text-3xl mb-2 block">🚒</span>
                <p className="text-foreground font-medium text-sm">{t('landing.donationCategories.oneSystem.firefighting')}</p>
              </div>
              <div className="text-center">
                <span className="text-3xl mb-2 block">🌱</span>
                <p className="text-foreground font-medium text-sm">{t('landing.donationCategories.oneSystem.reforestation')}</p>
              </div>
              <div className="text-center">
                <span className="text-3xl mb-2 block">♻️</span>
                <p className="text-foreground font-medium text-sm">{t('landing.donationCategories.oneSystem.contamination')}</p>
              </div>
              <div className="text-center">
                <span className="text-3xl mb-2 block">🏛️</span>
                <p className="text-foreground font-medium text-sm">{t('landing.donationCategories.oneSystem.biosphere')}</p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('landing.donationCategories.oneSystem.together')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* General Roadmap / Trajectory Section */}
      <section className="py-24 px-4 bg-background" id="roadmap">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Leaf className="w-3.5 h-3.5" />
              {t('roadmap.thePlan')}
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              {t('roadmap.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('roadmap.subtitle')}
            </p>
          </motion.div>

          {/* Horizontal Timeline - Our Trajectory */}
          <div className="mb-16">
            <div className="grid md:grid-cols-4 gap-6">
              {/* Year 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="relative"
              >
                <div className="bg-card rounded-2xl p-6 border-2 border-green-500/50 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</span>
                    <span className="text-xs font-semibold uppercase text-green-600">2025</span>
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-600 text-xs rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      Active
                    </span>
                  </div>
                  <h3 className="font-bold text-foreground mb-2">Foundation & Launch</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Legal foundation</li>
                    <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Website & transparency</li>
                    <li className="flex items-center gap-2"><span className="text-amber-500">◐</span> First equipment</li>
                    <li className="flex items-center gap-2"><span className="text-muted-foreground">○</span> 10,000 trees planted</li>
                  </ul>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-green-500 to-primary"></div>
              </motion.div>

              {/* Year 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <div className="bg-card rounded-2xl p-6 border border-border h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">2</span>
                    <span className="text-xs font-semibold uppercase text-primary">2026</span>
                  </div>
                  <h3 className="font-bold text-foreground mb-2">Scale & Expand</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>○ 30,000 trees total</li>
                    <li>○ Fire response active</li>
                    <li>○ 100 hectares de-pined</li>
                    <li>○ Community training</li>
                  </ul>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary/50 to-primary/30"></div>
              </motion.div>

              {/* Year 3-4 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="relative"
              >
                <div className="bg-card rounded-2xl p-6 border border-border h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-foreground font-bold text-sm">3</span>
                    <span className="text-xs font-semibold uppercase text-muted-foreground">2027-28</span>
                  </div>
                  <h3 className="font-bold text-foreground mb-2">Full Operations</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>○ 50,000+ trees</li>
                    <li>○ 33ha biosphere park</li>
                    <li>○ Fire prevention network</li>
                    <li>○ Education center</li>
                  </ul>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-muted"></div>
              </motion.div>

              {/* Long-term */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="bg-card rounded-2xl p-6 border border-border h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-foreground font-bold text-sm">∞</span>
                    <span className="text-xs font-semibold uppercase text-muted-foreground">2029+</span>
                  </div>
                  <h3 className="font-bold text-foreground mb-2">Permanent Model</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>○ Self-sustaining system</li>
                    <li>○ Replicate to other areas</li>
                    <li>○ Open-source platform</li>
                    <li>○ Carbon certification</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Overall Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-8 bg-gradient-to-r from-card via-card to-card border">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">{t('roadmap.totalGoal')}</h3>
                <p className="text-muted-foreground">Total raised across all categories</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6">
                {categories.map((cat) => {
                  const colorMap = {
                    amber: 'text-amber-600',
                    red: 'text-red-600',
                    green: 'text-green-600',
                    blue: 'text-blue-600',
                    purple: 'text-purple-600'
                  };
                  const routeMap = {
                    depining: '/donate/depining',
                    firefighting: '/donate/firefighting',
                    reforestation: '/donate/reforestation',
                    research: '/donate/contamination',
                    biosphere: '/biosphere'
                  };
                  return (
                    <Link 
                      key={cat.slug} 
                      to={routeMap[cat.slug] || `/donate/${cat.slug}`} 
                      className="text-center hover:scale-105 transition-transform"
                    >
                      <span className="text-3xl block mb-2">{cat.icon}</span>
                      <p className="font-medium text-foreground text-sm">{cat.name_en}</p>
                      <p className={`text-xl font-bold ${colorMap[cat.color] || 'text-primary'}`}>
                        ${(cat.raised || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        of ${cat.goal >= 1000000 ? `${(cat.goal / 1000000).toFixed(1)}M` : cat.goal.toLocaleString()}
                      </p>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-6">
                {(() => {
                  const totalGoal = categories.reduce((sum, c) => sum + (c.goal || 0), 0);
                  const totalRaised = categories.reduce((sum, c) => sum + (c.raised || 0), 0);
                  const percentage = totalGoal > 0 ? Math.round((totalRaised / totalGoal) * 100 * 10) / 10 : 0;
                  return (
                    <>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium">Total Raised: ${totalRaised.toLocaleString()}</span>
                        <span className="text-muted-foreground">
                          Goal: ${totalGoal >= 1000000 ? `${(totalGoal / 1000000).toFixed(1)}M` : totalGoal.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-4 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 via-red-500 via-green-500 to-purple-500 rounded-full transition-all duration-1000" 
                          style={{ width: `${Math.min(percentage, 100)}%` }} 
                        />
                      </div>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        {percentage}% — {t('roadmap.everyContribution')}
                      </p>
                    </>
                  );
                })()}
              </div>
            </Card>
          </motion.div>

          {/* Detailed Phases CTA */}
          <motion.div 
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-muted-foreground mb-4">
              Each donation category has its own detailed phases and milestones.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/donate/depining">
                <Button variant="outline" className="gap-2">
                  🟫 De-Pining <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/donate/firefighting">
                <Button variant="outline" className="gap-2">
                  🚒 Firefighting <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/donate/reforestation">
                <Button variant="outline" className="gap-2">
                  🌱 Reforestation <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/donate/contamination">
                <Button variant="outline" className="gap-2">
                  ♻️ Waste Mgmt <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-4 bg-card texture-overlay">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="grid md:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp}>
              <div className="eco-badge mb-4">
                <Leaf className="w-3.5 h-3.5" />
                {t('landing.missionTitle')}
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-foreground" data-testid="mission-title">
                {t('landing.restoringPatagonia')} <span className="text-primary">{t('landing.patagonia')}</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t('landing.missionText')}
              </p>
              <div className="section-divider !mx-0" />
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-primary/10 rounded-lg" />
              <img 
                src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                alt="Planting trees"
                className="relative rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 bg-background">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <TreeDeciduous className="w-3.5 h-3.5" />
              {t('landing.simpleProcess')}
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground" data-testid="how-it-works-title">
              {t('landing.howItWorksTitle')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              { icon: Map, title: t('landing.step1Title'), text: t('landing.step1Text'), num: '01' },
              { icon: TreeDeciduous, title: t('landing.step2Title'), text: t('landing.step2Text'), num: '02' },
              { icon: Eye, title: t('landing.step3Title'), text: t('landing.step3Text'), num: '03' },
            ].map((step, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <Card className="p-8 h-full natural-card hover:shadow-xl transition-all group border-border/50">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <step.icon className="w-7 h-7 text-primary" />
                    </div>
                    <span className="text-5xl font-bold text-primary/10 font-serif">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.text}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-24 px-4 eco-gradient text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold" data-testid="impact-title">
              {t('landing.impactTitle')}
            </h2>
            <div className="w-24 h-1 bg-white/30 rounded-full mx-auto mt-6" />
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              { value: stats.treesPlanted, label: t('landing.treesPlanted'), icon: TreeDeciduous },
              { value: stats.treesAdopted, label: t('landing.treesAdopted'), icon: Award },
              { value: stats.donors, label: t('landing.donorsCount'), icon: Users },
              { value: stats.co2Offset, label: t('landing.co2Offset'), icon: Leaf },
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                variants={fadeInUp}
                className="text-center"
                data-testid={`impact-stat-${i}`}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                  <stat.icon className="w-8 h-8 opacity-90" />
                </div>
                <div className="big-number mb-2">{stat.value}</div>
                <p className="text-sm uppercase tracking-wider opacity-80">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Transparency CTA Section */}
      <section className="py-24 px-4 bg-card texture-overlay">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-foreground" data-testid="transparency-title">
              {t('landing.transparencyTitle')}
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('landing.transparencyText')}
            </p>
            <Button 
              asChild 
              size="lg" 
              className="btn-primary text-lg"
              data-testid="transparency-cta"
            >
              <Link to="/transparency">
                {t('landing.viewTransparency')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 eco-gradient text-white/80">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <TreeDeciduous className="w-5 h-5" />
              </div>
              <span className="font-semibold text-lg">Epuyén Renace</span>
            </div>
            <p className="text-sm opacity-70">
              © {new Date().getFullYear()} Epuyén Renace. {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
